import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSubjectDetail, createSubject, updateSubject } from '../api/subjectsApi';
import { appToast } from '../../../../components/AppToast';

export function useSubjectsEntryScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editingSubjectId = searchParams.get('id');

  const [form, setForm] = useState({
    name: '',
    short_name: '',
  });

  const [operationalStatus, setOperationalStatus] = useState('active');
  const [isLoading, setIsLoading] = useState(Boolean(editingSubjectId));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load existing subject if editing
  useEffect(() => {
    async function loadIfEditing() {
      if (!editingSubjectId) return;
      setIsLoading(true);
      setError('');
      try {
        const detail = await getSubjectDetail(editingSubjectId);
        if (detail) {
          setForm({
            name: detail.name || '',
            short_name: detail.short_name || '',
          });
          setOperationalStatus(detail.status === 'Active' ? 'active' : 'inactive');
        } else {
          setError('Subject not found.');
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Failed to load subject details.';
        setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      } finally {
        setIsLoading(false);
      }
    }
    loadIfEditing();
  }, [editingSubjectId]);

  const patchForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Subject Name is required.');
      appToast.error('Subject Name is required.');
      return;
    }
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        name: form.name.trim(),
        short_name: form.short_name ? form.short_name.trim() : null,
        status: operationalStatus === 'active' ? 'Active' : 'Archive',
      };

      if (editingSubjectId) {
        await updateSubject(editingSubjectId, payload);
        appToast.success('Subject updated successfully.');
        setSuccess('Subject updated successfully.');
      } else {
        await createSubject(payload);
        appToast.success('Subject created successfully.');
        setSuccess('Subject created successfully.');
      }

      setTimeout(() => {
        navigate('/masters/subjects/list');
      }, 600);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to save subject.';
      const errStr = typeof msg === 'string' ? msg : JSON.stringify(msg);
      setError(errStr);
      appToast.error(errStr);
    } finally {
      setIsSaving(false);
    }
  };

  const completionPercent = useMemo(() => {
    const fields = ['name', 'short_name'];
    const filled = fields.filter((f) => String(form[f] || '').trim().length > 0).length;
    return Math.round((filled / fields.length) * 100);
  }, [form]);

  return {
    state: {
      form,
      operationalStatus,
      isLoading,
      isSaving,
      error,
      success,
      isEditMode: Boolean(editingSubjectId),
      completionPercent,
    },
    actions: {
      patchForm,
      setOperationalStatus,
      handleSave,
    },
  };
}
